import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Buildings, Lock, Upload, Image as ImageIcon, X, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, withAuth } from "@/context/AuthContext";

const TIMEZONES = ["Asia/Kolkata","Asia/Dubai","Asia/Singapore","Europe/London","America/New_York","America/Los_Angeles","Australia/Sydney"];
const CURRENCIES = ["INR","USD","EUR","GBP","AED","SGD","AUD","CAD"];
const INDUSTRIES = ["Retail","Wholesale","Manufacturing","Distribution","Services","Real Estate","Healthcare","Education","Hospitality","Technology","Consumer Goods","Other"];
const BUSINESS_TYPES = ["Proprietorship","Partnership","LLP","Private Limited","Public Limited","Section 8","HUF","Other"];

function Field({ label, required, hint, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-xs flex items-center gap-1">
        {label.toUpperCase()}
        {required && <span className="text-destructive">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function UploadField({ label, value, onChange, accept = "image/*", testId }) {
  const [busy, setBusy] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large — max 5MB");
      e.target.value = ""; return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };
  const isPdf = value && (value.endsWith(".pdf") || value.includes("application/pdf"));
  const preview = value ? withAuth(value) : null;
  return (
    <div data-testid={testId}>
      <Label className="text-xs">{label.toUpperCase()}</Label>
      <div className="mt-1.5">
        {value ? (
          <div className="relative inline-flex items-center gap-3 p-2 pr-8 rounded-md border border-border bg-secondary/40">
            {value.startsWith("data:image") || (!isPdf && value) ? (
              <img src={preview} alt={label} className="w-12 h-12 object-contain rounded bg-white p-1" />
            ) : (
              <FilePdf size={28} weight="duotone" className="text-muted-foreground" />
            )}
            <span className="text-xs text-muted-foreground truncate max-w-[180px]">Uploaded</span>
            <button type="button" onClick={() => onChange("")} className="absolute top-1 right-1 p-1 text-muted-foreground hover:text-destructive" aria-label="remove">
              <X size={12} />
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-border hover:border-primary hover:bg-secondary/40 cursor-pointer text-sm">
            <Upload size={14} />
            <span>{busy ? "Uploading…" : "Choose file"}</span>
            <input type="file" accept={accept} onChange={handleFile} className="hidden" />
          </label>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, refreshOrg } = useAuth();
  const isOwner = user?.role === "organization_owner";
  const [org, setOrg] = useState(null);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/organization");
    setOrg(r.data); setF(r.data || {});
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!f.name?.trim()) return toast.error("Company name is required");
    if (!f.primary_mobile?.trim()) return toast.error("Primary mobile is required");
    if (!f.primary_email?.trim()) return toast.error("Primary email is required");
    if (!f.state?.trim()) return toast.error("State is required");
    if (!f.country?.trim()) return toast.error("Country is required");
    setSaving(true);
    try {
      const r = await api.put("/organization", f);
      setOrg(r.data);
      if (refreshOrg) await refreshOrg();
      toast.success("Organization profile saved");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (Array.isArray(d)) {
        toast.error(d[0]?.msg || "Validation failed");
      } else {
        toast.error(typeof d === "string" ? d : "Save failed");
      }
    } finally { setSaving(false); }
  };

  if (!org) return <div className="text-muted-foreground p-8">Loading…</div>;

  const disabled = !isOwner;

  return (
    <div className="space-y-8 max-w-5xl" data-testid="settings-page">
      <div>
        <div className="overline text-muted-foreground mb-2">Workspace</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Organization Profile</h1>
        <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
          <Buildings size={14} weight="duotone" />
          <span>Tenant <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-secondary">{org.id?.slice(0,8)}</span></span>
        </div>
      </div>

      {!isOwner && (
        <Card className="p-5 border-primary/30 bg-primary/5">
          <div className="flex items-start gap-3">
            <Lock size={18} weight="duotone" className="text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-sm">Read-only view</div>
              <div className="text-xs text-muted-foreground mt-1">Only the organization owner can edit this profile.</div>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6 space-y-5">
        <div>
          <div className="overline text-muted-foreground mb-1">Section 01</div>
          <h2 className="font-heading font-bold text-xl">Company Basics</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Company / Firm Name" required testId="field-name">
            <Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} disabled={disabled} data-testid="input-name" />
          </Field>
          <Field label="Brand Name" testId="field-brand">
            <Input value={f.brand_name || ""} onChange={(e) => set("brand_name", e.target.value)} disabled={disabled} />
          </Field>
          <Field label="Primary Mobile" required testId="field-mobile">
            <Input value={f.primary_mobile || ""} onChange={(e) => set("primary_mobile", e.target.value)} disabled={disabled} />
          </Field>
          <Field label="Primary Email" required testId="field-email">
            <Input type="email" value={f.primary_email || ""} onChange={(e) => set("primary_email", e.target.value)} disabled={disabled} />
          </Field>
          <Field label="WhatsApp Number" testId="field-whatsapp">
            <Input value={f.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} disabled={disabled} />
          </Field>
          <Field label="Website URL" testId="field-website">
            <Input placeholder="https://" value={f.website || ""} onChange={(e) => set("website", e.target.value)} disabled={disabled} />
          </Field>
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div>
          <div className="overline text-muted-foreground mb-1">Section 02</div>
          <h2 className="font-heading font-bold text-xl">Legal & Tax</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="GST Number" testId="field-gst"><Input value={f.gst_number || ""} onChange={(e) => set("gst_number", e.target.value)} disabled={disabled} /></Field>
          <Field label="PAN Number" testId="field-pan"><Input value={f.pan_number || ""} onChange={(e) => set("pan_number", e.target.value)} disabled={disabled} /></Field>
          <Field label="CIN / Reg. Number" testId="field-cin"><Input value={f.cin_number || ""} onChange={(e) => set("cin_number", e.target.value)} disabled={disabled} /></Field>
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div>
          <div className="overline text-muted-foreground mb-1">Section 03</div>
          <h2 className="font-heading font-bold text-xl">Address</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Billing Address" testId="field-billing"><Textarea rows={2} value={f.billing_address || ""} onChange={(e) => set("billing_address", e.target.value)} disabled={disabled} /></Field>
          <Field label="Shipping Address" testId="field-shipping"><Textarea rows={2} value={f.shipping_address || ""} onChange={(e) => set("shipping_address", e.target.value)} disabled={disabled} /></Field>
          <Field label="City" testId="field-city"><Input value={f.city || ""} onChange={(e) => set("city", e.target.value)} disabled={disabled} /></Field>
          <Field label="State" required testId="field-state"><Input value={f.state || ""} onChange={(e) => set("state", e.target.value)} disabled={disabled} /></Field>
          <Field label="Pincode" testId="field-pincode"><Input value={f.pincode || ""} onChange={(e) => set("pincode", e.target.value)} disabled={disabled} /></Field>
          <Field label="Country" required testId="field-country"><Input value={f.country || ""} onChange={(e) => set("country", e.target.value)} disabled={disabled} placeholder="India" /></Field>
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div>
          <div className="overline text-muted-foreground mb-1">Section 04</div>
          <h2 className="font-heading font-bold text-xl">Preferences</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Time Zone">
            <Select value={f.timezone || ""} onValueChange={(v) => set("timezone", v)} disabled={disabled}>
              <SelectTrigger><SelectValue placeholder="Choose timezone" /></SelectTrigger>
              <SelectContent>{TIMEZONES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Currency">
            <Select value={f.currency || ""} onValueChange={(v) => set("currency", v)} disabled={disabled}>
              <SelectTrigger><SelectValue placeholder="Choose currency" /></SelectTrigger>
              <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Business Type">
            <Select value={f.business_type || ""} onValueChange={(v) => set("business_type", v)} disabled={disabled}>
              <SelectTrigger><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>{BUSINESS_TYPES.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Industry Category">
            <Select value={f.industry || ""} onValueChange={(v) => set("industry", v)} disabled={disabled}>
              <SelectTrigger><SelectValue placeholder="Choose industry" /></SelectTrigger>
              <SelectContent>{INDUSTRIES.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="md:col-span-2">
            <Field label="About / Description">
              <Textarea rows={3} value={f.description || ""} onChange={(e) => set("description", e.target.value)} disabled={disabled} placeholder="Tell customers who you are…" />
            </Field>
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div>
          <div className="overline text-muted-foreground mb-1">Section 05</div>
          <h2 className="font-heading font-bold text-xl">Bank & Payment Details</h2>
          <p className="text-xs text-muted-foreground mt-1">Printed on Tax Invoice PDFs. Leave blank to hide.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Account Holder Name" testId="field-acholder"><Input value={f.account_holder_name || ""} onChange={(e) => set("account_holder_name", e.target.value)} disabled={disabled} data-testid="input-acholder" /></Field>
          <Field label="Bank Name" testId="field-bank"><Input value={f.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} disabled={disabled} data-testid="input-bank" /></Field>
          <Field label="Bank Account Number" testId="field-acno"><Input value={f.bank_account_number || ""} onChange={(e) => set("bank_account_number", e.target.value)} disabled={disabled} data-testid="input-acno" /></Field>
          <Field label="IFSC Code" testId="field-ifsc"><Input value={f.bank_ifsc || ""} onChange={(e) => set("bank_ifsc", e.target.value.toUpperCase())} disabled={disabled} data-testid="input-ifsc" placeholder="ABCD0XXXXXX" /></Field>
          <Field label="Branch" testId="field-branch"><Input value={f.bank_branch || ""} onChange={(e) => set("bank_branch", e.target.value)} disabled={disabled} /></Field>
          <Field label="UPI ID" testId="field-upi"><Input value={f.upi_id || ""} onChange={(e) => set("upi_id", e.target.value)} disabled={disabled} placeholder="brand@upi" /></Field>
          <div className="md:col-span-2">
            <Field label="Invoice Terms & Conditions (footer)" testId="field-terms">
              <Textarea rows={4} value={f.invoice_terms || ""} onChange={(e) => set("invoice_terms", e.target.value)} disabled={disabled} placeholder="1. Goods once sold will not be taken back or exchanged.&#10;2. Interest @18% p.a. will be charged on overdue invoices." />
            </Field>
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div>
          <div className="overline text-muted-foreground mb-1">Section 06</div>
          <h2 className="font-heading font-bold text-xl">Social Links</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Facebook"><Input value={f.facebook || ""} onChange={(e) => set("facebook", e.target.value)} disabled={disabled} placeholder="https://facebook.com/…" /></Field>
          <Field label="Instagram"><Input value={f.instagram || ""} onChange={(e) => set("instagram", e.target.value)} disabled={disabled} /></Field>
          <Field label="LinkedIn"><Input value={f.linkedin || ""} onChange={(e) => set("linkedin", e.target.value)} disabled={disabled} /></Field>
          <Field label="YouTube"><Input value={f.youtube || ""} onChange={(e) => set("youtube", e.target.value)} disabled={disabled} /></Field>
          <Field label="X (Twitter)"><Input value={f.twitter || ""} onChange={(e) => set("twitter", e.target.value)} disabled={disabled} /></Field>
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div>
          <div className="overline text-muted-foreground mb-1">Section 07</div>
          <h2 className="font-heading font-bold text-xl">Brand & Documents</h2>
          <p className="text-xs text-muted-foreground mt-1">PNG/JPG, max 800KB per file.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <UploadField label="Company Logo" value={f.logo} onChange={(v) => set("logo", v)} testId="upload-logo" />
          <UploadField label="Letterhead" value={f.letterhead} onChange={(v) => set("letterhead", v)} testId="upload-letterhead" />
          <UploadField label="Digital Signature" value={f.signature} onChange={(v) => set("signature", v)} testId="upload-signature" />
          <UploadField label="GST Certificate" value={f.gst_certificate} onChange={(v) => set("gst_certificate", v)} accept="image/*,application/pdf" testId="upload-gst-cert" />
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div>
          <div className="overline text-muted-foreground mb-1">About</div>
          <h2 className="font-heading font-bold text-xl">App & License</h2>
        </div>
        <AboutBox />
      </Card>

      {isOwner && (
        <div className="flex justify-end gap-2 sticky bottom-4">
          <Button variant="outline" onClick={() => setF(org)} data-testid="reset-button">Reset</Button>
          <Button onClick={save} disabled={saving} className="rounded-full" data-testid="save-org-button">
            {saving ? "Saving…" : "Save profile"}
          </Button>
        </div>
      )}
    </div>
  );
}

function AboutBox() {
  const [v, setV] = useState(null);
  useEffect(() => { api.get("/version").then((r) => setV(r.data)); }, []);
  if (!v) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="about-box">
      <div><div className="overline text-muted-foreground text-[10px]">App</div><div className="font-medium mt-1">{v.app}</div></div>
      <div><div className="overline text-muted-foreground text-[10px]">Version</div><div className="font-mono mt-1">v{v.version}</div></div>
      <div><div className="overline text-muted-foreground text-[10px]">Release Date</div><div className="mt-1">{new Date(v.release_date).toLocaleDateString("en-IN")}</div></div>
      <div><div className="overline text-muted-foreground text-[10px]">Plan</div><div className="mt-1">Free · Active</div></div>
      <div className="col-span-2 md:col-span-4 text-xs text-muted-foreground mt-2">{v.copyright}</div>
    </div>
  );
}
